import {
  type BlockWireframe,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import { call, put } from 'redux-saga/effects'

import { addFilledBlock, addFilledChild } from '@/hooks/ai/actionCreators'
import type { XmlImportNodeItem } from '@/utils/xml'
import { waitForDependency } from './waits'

export function* addNodeRecursive({
  node,
  parentId,
  index,
}: {
  node: XmlImportNodeItem
  parentId?: string
  index: number
}): Generator<any, void, any> {
  const nodeId = node.id

  yield put(
    addFilledChild({
      id: nodeId,
      parentId,
      name: node.title || 'Untitled',
      title: node.seo.title || '',
      description: node.seo.description || '',
      h1: node.seo.h1 || '',
      keywords: node.seo.keywords || '',
      pageIntent: node.seo.pageIntent || '',
      slug: node.seo.slug || '',
      url: '',
      index,
      ...(node.colorId ? { colorId: node.colorId } : {}),
    })
  )

  yield call(waitForDependency, {
    id: nodeId,
    type: 'node',
  })

  for (const tagId of node.tagIds) {
    yield put(
      ProjectActions.tags.addNode({
        id: tagId,
        item: nodeId,
      })
    )
  }

  for (const [blockIndex, block] of node.blocks.entries()) {
    const blockId = block.id

    yield put(
      addFilledBlock({
        id: blockId,
        nodeId,
        title: block.title || 'Untitled',
        index: blockIndex,
        ...(block.colorId ? { colorId: block.colorId } : {}),
        ...(block.wireframes?.length
          ? {
              wireframes: block.wireframes as BlockWireframe[],
            }
          : {}),
      })
    )

    yield call(waitForDependency, {
      id: blockId,
      type: 'block',
    })

    if (block.content) {
      yield put(
        ProjectActions.blocks.updateContent({
          id: blockId,
          content: block.content,
        })
      )
    }
  }

  for (const [childIndex, childNode] of node.children.entries()) {
    yield call(addNodeRecursive, {
      node: childNode,
      parentId: nodeId,
      index: childIndex,
    })
  }
}
